import { AbstractDecoratorService } from '@abstract-services/decorator/1.0/decorator.abstract-service';
import * as ngCommonHttp from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DecoratorAPI } from '@constants/api';
import {
  Decorator,
  DecoratorDeleteFilters,
  DecoratorsResponse,
  DecoratorType,
} from '@interfaces/decorator/1.0';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, concatMap, map, reduce } from 'rxjs/operators';
import { SKIP_TENANT_HEADER } from '@interceptors/add-tenant-header/1.0/skip-tenant-header.token';

@Injectable({
  providedIn: 'root',
})
export class DecoratorService implements AbstractDecoratorService {
  apiUrl: string;

  constructor(private http: ngCommonHttp.HttpClient) {
    this.apiUrl = DecoratorAPI.GET;
  }

  get(brandId: string, entityId: string, skipTenantHeader = false): Observable<Decorator[]> {
    const URL = this.apiUrl.replace(':tenantId', brandId).replace(':entityId', entityId);
    const headers = new ngCommonHttp.HttpHeaders({
      'Cache-Control': 'no-cache, no-store, must-revalidate',
    });
    const requestOptions = {
      ...this.buildSkipTenantHeaderOptions(skipTenantHeader),
      headers,
    };

    return this.http.get(URL, requestOptions).pipe(
      map((response: DecoratorsResponse) => {
        return response.decorators;
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => {
        // catch 404 and return empty array
        if (err.status === 404) {
          return of([]);
        } else {
          return throwError(err);
        }
      }),
    );
  }

  getBrandLogoUrl(brandId: string): Observable<string | null> {
    return this.get(brandId, brandId).pipe(
      map((decorators) => {
        return decorators?.find((d) => d.field === 'images' && d.key === 'logo')?.value || null;
      }),
    );
  }

  save(
    brandId: string,
    entityId: string,
    decorators: Decorator[],
    type: DecoratorType,
  ): Observable<Decorator[]> {
    if (typeof type === 'undefined') {
      throw new Error('decoratorType is required');
    }
    const URL = this.apiUrl.replace(':tenantId', brandId).replace(':entityId', entityId);

    // only save entries with values
    const saveData = decorators
      .filter((d) => !!d.value?.trim() || d.field === 'images')
      .map((item) => ({ ...item, type: item.enforceType || type }));

    return this.http.put(URL, saveData).pipe(
      map((response: DecoratorsResponse) => {
        return response.decorators;
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  saveBulk(
    brandId: string,
    entityId: string,
    decorators: Decorator[],
    type: DecoratorType,
  ): Observable<Decorator[]> {
    if (typeof type === 'undefined') {
      throw new Error('decoratorType is required');
    }
    const URL = DecoratorAPI.BULK.replace(':tenantId', brandId);
    const BULK_CHUNK_SIZE = 10;

    const saveData: Decorator[] = decorators
      .filter((d) => !!d.value?.trim() || d.field === 'images' || !!d.multimedia)
      .map((item) => ({
        field: item.field,
        key: item.key,
        value: item.value,
        multimedia: item.multimedia,
        extension: item.extension,
        type: item.enforceType || type,
        entityId,
      }));

    if (!saveData.length) {
      return of([]);
    }

    const chunks: Decorator[][] = [];
    for (let i = 0; i < saveData.length; i += BULK_CHUNK_SIZE) {
      chunks.push(saveData.slice(i, i + BULK_CHUNK_SIZE));
    }

    return from(chunks).pipe(
      concatMap((chunk) => {
        return this.http.post(URL, chunk).pipe(
          map((response: DecoratorsResponse) => response.decorators),
          catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
        );
      }),
      reduce((acc: Decorator[], result: Decorator[]) => acc.concat(result), []),
    );
  }

  delete(brandId: string, entityId: string, filters?: DecoratorDeleteFilters): Observable<any> {
    const URL = this.apiUrl.replace(':tenantId', brandId).replace(':entityId', entityId);
    const params = this.buildDeleteParams(filters);
    const options = params.keys().length ? { params } : undefined;

    return this.http.delete(URL, options).pipe(
      map((response: any) => {
        return response;
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  private buildDeleteParams(filters?: DecoratorDeleteFilters): ngCommonHttp.HttpParams {
    let params = new ngCommonHttp.HttpParams();
    if (filters?.field) {
      params = params.set('field', filters.field);
    }
    if (filters?.key) {
      params = params.set('key', filters.key);
    }
    if (filters?.locale) {
      params = params.set('locale', filters.locale);
    }
    return params;
  }

  private buildSkipTenantHeaderOptions(skipTenantHeader = false) {
    return skipTenantHeader
      ? { context: new ngCommonHttp.HttpContext().set(SKIP_TENANT_HEADER, true) }
      : undefined;
  }
}
